import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import {
  FileText,
  ArrowLeft,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Calendar,
  Users,
  Euro,
  Shield,
  ChevronRight,
  FileSearch,
  Building,
  MapPin,
  Target,
  TrendingUp,
  Settings2,
  Gauge,
  Layers,
  Info,
  Hammer,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';

export function ProjectDetailPage() {
  const { id } = useParams();
  const { api } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const fetchProject = useCallback(async () => {
    try {
      const response = await api.get(`/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      console.error('Failed to fetch project:', error);
      toast.error('Erreur lors du chargement du projet');
    } finally {
      setLoading(false);
    }
  }, [api, id]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // Poll for updates if processing
  useEffect(() => {
    if (project?.status === 'processing') {
      const interval = setInterval(fetchProject, 5000);
      return () => clearInterval(interval);
    }
  }, [project?.status, fetchProject]);

  const handleReanalyze = async () => {
    setReanalyzing(true);
    try {
      await api.post(`/projects/${id}/reanalyze`);
      toast.success('Analyse relancée');
      fetchProject();
    } catch (error) {
      toast.error("Erreur lors du relancement de l'analyse");
    } finally {
      setReanalyzing(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await api.get(`/projects/${id}/export-pdf`, {
        responseType: 'blob'
      });
      
      // Créer un lien de téléchargement
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extraire le nom du fichier depuis les headers ou utiliser un nom par défaut
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'rapport_dce.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exporté avec succès');
    } catch (error) {
      console.error('Export PDF error:', error);
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  const getVerdictStyle = (verdict) => {
    if (verdict === 'GO') {
      return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: CheckCircle2, iconBg: 'bg-green-500' };
    } else if (verdict === 'NO_GO') {
      return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: XCircle, iconBg: 'bg-red-500' };
    }
    return { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: AlertTriangle, iconBg: 'bg-orange-500' };
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence === 'HIGH') return <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">Haute</span>;
    if (confidence === 'MEDIUM') return <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">Moyenne</span>;
    if (confidence === 'NOT_FOUND') return <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded">Non détecté</span>;
    return <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Estimé</span>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="text-center py-16">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 mb-4">Projet non trouvé</p>
          <Link to="/projects">
            <Button variant="outline">Retour aux projets</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const analysis = project.analysis;
  const verdictStyle = analysis ? getVerdictStyle(analysis.verdict) : null;

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6">
        <Link to="/projects" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4" data-testid="back-to-projects">
          <ArrowLeft size={16} />
          Retour aux projets
        </Link>
        
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">{project.title}</h1>
            <p className="text-slate-500 text-sm mt-1">
              {project.filename} • {formatFileSize(project.file_size)} • {project.page_count || '?'} pages
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {project.status === 'completed' && analysis && (
              <Button 
                onClick={handleExportPdf} 
                disabled={exportingPdf}
                variant="outline"
                className="gap-2"
                data-testid="export-pdf-btn"
              >
                {exportingPdf ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Download size={18} />
                )}
                Exporter en PDF
              </Button>
            )}
            {project.status === 'failed' && (
              <Button onClick={handleReanalyze} disabled={reanalyzing} className="gap-2">
                <RefreshCw size={18} className={reanalyzing ? 'animate-spin' : ''} />
                Relancer l'analyse
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Processing State */}
      {project.status === 'processing' && (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-6 flex items-center gap-4">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <div>
              <h3 className="font-semibold text-slate-900">Analyse en cours...</h3>
              <p className="text-slate-600 text-sm">L'IA analyse votre document DCE. Cela peut prendre 1-2 minutes.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Failed State */}
      {project.status === 'failed' && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="p-6 flex items-center gap-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
            <div>
              <h3 className="font-semibold text-slate-900">Échec de l'analyse</h3>
              <p className="text-slate-600 text-sm">{project.error || 'Une erreur s\'est produite. Veuillez réessayer.'}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analysis Results */}
      {project.status === 'completed' && analysis && (
        <div className="space-y-6">
          {/* Verdict Banner */}
          <Card className={`border-2 ${verdictStyle.border} ${verdictStyle.bg}`}>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 ${verdictStyle.iconBg} rounded-xl flex items-center justify-center`}>
                    <verdictStyle.icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className={`font-heading text-2xl font-bold ${verdictStyle.text}`}>
                      {analysis.verdict === 'GO' ? 'GO RECOMMANDÉ' : analysis.verdict === 'NO_GO' ? 'NO GO' : 'VIGILANCE REQUISE'}
                    </h2>
                    <p className="text-slate-600 mt-1">{analysis.explanation}</p>
                  </div>
                </div>
                <div className="text-center lg:text-right">
                  <p className="text-sm text-slate-500">Score global</p>
                  <p className={`text-4xl font-bold ${verdictStyle.text}`}>
                    {analysis.score}<span className="text-xl text-slate-400">/100</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Two Questions */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className={analysis.should_respond ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500'}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-slate-600">Devez-vous répondre ?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-2xl font-bold ${analysis.should_respond ? 'text-green-600' : 'text-red-600'}`}>
                  {analysis.should_respond ? 'OUI' : 'NON'}
                </p>
                {analysis.should_respond_reasons?.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {analysis.should_respond_reasons.map((r, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <ChevronRight size={14} className="mt-1 flex-shrink-0 text-slate-400" />{r}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card className={analysis.can_execute ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500'}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-slate-600">Pouvez-vous exécuter ?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-2xl font-bold ${analysis.can_execute ? 'text-green-600' : 'text-red-600'}`}>
                  {analysis.can_execute ? 'OUI' : 'NON'}
                </p>
                {analysis.can_execute_reasons?.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {analysis.can_execute_reasons.map((r, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <ChevronRight size={14} className="mt-1 flex-shrink-0 text-slate-400" />{r}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Project Info */}
          {analysis.project_info && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Building size={20} />
                  Synthèse du projet
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {analysis.project_info.title && (
                    <div>
                      <p className="text-sm text-slate-500">Titre</p>
                      <p className="font-medium text-slate-900">{analysis.project_info.title}</p>
                    </div>
                  )}
                  {analysis.project_info.client && (
                    <div>
                      <p className="text-sm text-slate-500">Maître d'ouvrage</p>
                      <p className="font-medium text-slate-900">{analysis.project_info.client}</p>
                    </div>
                  )}
                  {analysis.project_info.location && (
                    <div>
                      <p className="text-sm text-slate-500">Localisation</p>
                      <p className="font-medium text-slate-900 flex items-center gap-1">
                        <MapPin size={14} />{analysis.project_info.location}
                      </p>
                    </div>
                  )}
                  {analysis.project_info.work_type && (
                    <div>
                      <p className="text-sm text-slate-500">Type de travaux</p>
                      <p className="font-medium text-slate-900">{analysis.project_info.work_type}</p>
                    </div>
                  )}
                  {analysis.project_info.surface && (
                    <div>
                      <p className="text-sm text-slate-500">Surface</p>
                      <p className="font-medium text-slate-900">{analysis.project_info.surface}</p>
                    </div>
                  )}
                  {analysis.project_info.estimated_amount && (
                    <div>
                      <p className="text-sm text-slate-500">Montant estimé</p>
                      <p className="font-medium text-slate-900">{analysis.project_info.estimated_amount}</p>
                    </div>
                  )}
                  {analysis.project_info.submission_deadline && (
                    <div>
                      <p className="text-sm text-slate-500">Date limite de remise</p>
                      <p className="font-medium text-slate-900 flex items-center gap-1">
                        <Calendar size={14} />{analysis.project_info.submission_deadline}
                      </p>
                    </div>
                  )}
                  {analysis.project_info.description && (
                    <div className="md:col-span-2 lg:col-span-3">
                      <p className="text-sm text-slate-500">Description</p>
                      <p className="font-medium text-slate-900">{analysis.project_info.description}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sub-scores */}
          {analysis.sub_scores && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Target size={20} />
                  Sous-scores détaillés
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: 'economic_attractiveness', label: 'Attractivité économique', desc: 'Ce marché est-il rentable ?' },
                  { key: 'contractual_risk', label: 'Risque contractuel (inversé)', desc: 'Les clauses sont-elles acceptables ?' },
                  { key: 'technical_feasibility', label: 'Faisabilité technique', desc: 'Avons-nous les compétences ?' },
                  { key: 'operational_capacity', label: 'Capacité opérationnelle', desc: 'Avons-nous les ressources ?' },
                ].map((item) => {
                  const score = analysis.sub_scores[item.key] || 0;
                  const barColor = score >= 70 ? 'bg-green-500' : score >= 40 ? 'bg-orange-500' : 'bg-red-500';
                  return (
                    <div key={item.key}>
                      <div className="flex justify-between mb-1">
                        <div>
                          <p className="font-medium text-slate-900">{item.label}</p>
                          <p className="text-xs text-slate-500">{item.desc}</p>
                        </div>
                        <span className="font-bold text-slate-900">{score}/100</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${barColor} rounded-full`} style={{ width: `${score}%` }} />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Traced Critical Info */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Penalties */}
            {analysis.penalties && (
              <TracedInfoCard
                icon={<Euro size={18} />}
                title="Pénalités de retard"
                data={analysis.penalties}
                getConfidenceBadge={getConfidenceBadge}
              />
            )}
            {/* Delays */}
            {analysis.delays && (
              <TracedInfoCard
                icon={<Calendar size={18} />}
                title="Délai d'exécution"
                data={analysis.delays}
                getConfidenceBadge={getConfidenceBadge}
              />
            )}
            {/* Guarantees */}
            {analysis.guarantees && (
              <TracedInfoCard
                icon={<Shield size={18} />}
                title="Garanties"
                data={analysis.guarantees}
                getConfidenceBadge={getConfidenceBadge}
              />
            )}
            {/* Selection Criteria */}
            {analysis.selection_criteria && (
              <TracedInfoCard
                icon={<FileSearch size={18} />}
                title="Critères de sélection"
                data={analysis.selection_criteria}
                getConfidenceBadge={getConfidenceBadge}
              />
            )}
          </div>

          {/* Alerts */}
          {analysis.alerts?.length > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                  <AlertTriangle size={20} />
                  Alertes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analysis.alerts.map((alert, i) => (
                    <div key={i} className={`p-3 rounded-lg ${alert.severity === 'HIGH' ? 'bg-red-100' : 'bg-orange-100'}`}>
                      <p className="font-medium text-slate-900">{alert.type}</p>
                      <p className="text-sm text-slate-600">{alert.message}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Risks */}
          {analysis.risks?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <AlertTriangle size={20} className="text-orange-500" />
                  Risques identifiés
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analysis.risks.map((risk, i) => (
                    <div key={i} className={`p-4 rounded-lg border ${
                      risk.severity === 'HIGH' ? 'border-red-200 bg-red-50' :
                      risk.severity === 'MEDIUM' ? 'border-orange-200 bg-orange-50' : 'border-slate-200 bg-slate-50'
                    }`}>
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium text-slate-900">{risk.type}</h4>
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${
                          risk.severity === 'HIGH' ? 'bg-red-200 text-red-800' :
                          risk.severity === 'MEDIUM' ? 'bg-orange-200 text-orange-800' : 'bg-slate-200 text-slate-800'
                        }`}>
                          {risk.severity === 'HIGH' ? 'ÉLEVÉ' : risk.severity === 'MEDIUM' ? 'MOYEN' : 'FAIBLE'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{risk.description}</p>
                      {risk.financial_impact && <p className="text-sm text-slate-500 mt-1">Impact: {risk.financial_impact}</p>}
                      {risk.recommendation && <p className="text-sm font-medium text-primary mt-2">→ {risk.recommendation}</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Planning */}
          {analysis.planning && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Calendar size={20} />
                  Planning macro
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Durée estimée</p>
                    <p className="text-2xl font-bold text-slate-900">{analysis.planning.total_duration_weeks} sem.</p>
                    <p className="text-xs text-slate-400">({analysis.planning.tolerance_min} - {analysis.planning.tolerance_max})</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Effectif moyen</p>
                    <p className="text-2xl font-bold text-slate-900">{analysis.planning.workforce_avg}</p>
                    <p className="text-xs text-slate-400">personnes</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Effectif en pointe</p>
                    <p className="text-2xl font-bold text-slate-900">{analysis.planning.workforce_peak}</p>
                    <p className="text-xs text-slate-400">personnes</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Heures totales</p>
                    <p className="text-2xl font-bold text-slate-900">{analysis.planning.total_hours?.toLocaleString()}</p>
                    <p className="text-xs text-slate-400">heures MO</p>
                  </div>
                </div>
                
                {/* Gantt-like */}
                <div className="space-y-2 mb-4">
                  {analysis.planning.phases?.map((phase, i) => {
                    const totalWeeks = analysis.planning.total_duration_weeks || 40;
                    const widthPercent = Math.max(5, (phase.duration_weeks / totalWeeks) * 100);
                    return (
                      <div key={i} className="flex items-center gap-4">
                        <div className="w-40 text-sm text-slate-600 truncate">{phase.name}</div>
                        <div className="flex-1 h-8 bg-slate-100 rounded overflow-hidden relative">
                          <div className="h-full bg-primary rounded flex items-center px-2" style={{ width: `${widthPercent}%` }}>
                            <span className="text-xs text-white font-medium whitespace-nowrap">{phase.duration_weeks} sem.</span>
                          </div>
                        </div>
                        <div className="w-16 text-sm text-slate-500 text-right">{phase.workforce} pers.</div>
                      </div>
                    );
                  })}
                </div>

                {/* Capacity validation */}
                <div className={`p-4 rounded-lg ${analysis.planning.capacity_validated ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    {analysis.planning.capacity_validated ? (
                      <CheckCircle2 className="text-green-600" size={20} />
                    ) : (
                      <AlertCircle className="text-red-600" size={20} />
                    )}
                    <span className={`font-medium ${analysis.planning.capacity_validated ? 'text-green-700' : 'text-red-700'}`}>
                      {analysis.planning.capacity_validated 
                        ? 'Capacité validée - Le projet peut être absorbé'
                        : 'Attention - Capacité insuffisante'}
                    </span>
                  </div>
                  {!analysis.planning.deadline_feasible && (
                    <p className="text-sm text-red-600 mt-2">
                      Le planning estimé ({analysis.planning.total_duration_weeks} sem.) dépasse le délai contractuel ({analysis.planning.contractual_weeks} sem.)
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ============================================================ */}
          {/* PREMIUM: Hypothèses de chiffrage détectées */}
          {/* ============================================================ */}
          {analysis.cost_estimation && (
            analysis.cost_estimation.detected_project_type_label ||
            analysis.cost_estimation.detected_region_label ||
            analysis.cost_estimation.detected_complexity_label
          ) && (
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Settings2 size={18} className="text-slate-500" />
                  Hypothèses de chiffrage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Type de projet */}
                  {analysis.cost_estimation.detected_project_type_label && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Building size={14} className="text-slate-400" />
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Type de projet</p>
                      </div>
                      <p className="font-medium text-slate-900">{analysis.cost_estimation.detected_project_type_label}</p>
                      {analysis.cost_estimation.detected_project_type_confidence !== undefined && (
                        <p className="text-xs text-slate-400 mt-1">
                          Confiance : {Math.round(analysis.cost_estimation.detected_project_type_confidence * 100)}%
                        </p>
                      )}
                    </div>
                  )}

                  {/* Région */}
                  {analysis.cost_estimation.detected_region_label && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin size={14} className="text-slate-400" />
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Région</p>
                      </div>
                      <p className="font-medium text-slate-900">{analysis.cost_estimation.detected_region_label}</p>
                      {analysis.cost_estimation.detected_region_coefficient && (
                        <p className="text-xs text-slate-400 mt-1">
                          Coefficient : ×{analysis.cost_estimation.detected_region_coefficient.toFixed(2)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Complexité */}
                  {analysis.cost_estimation.detected_complexity_label && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Gauge size={14} className="text-slate-400" />
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Complexité</p>
                      </div>
                      <p className="font-medium text-slate-900">{analysis.cost_estimation.detected_complexity_label}</p>
                      {analysis.cost_estimation.detected_complexity_coefficient && (
                        <p className="text-xs text-slate-400 mt-1">
                          Coefficient : ×{analysis.cost_estimation.detected_complexity_coefficient.toFixed(2)}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Méthode d'estimation */}
                {analysis.cost_estimation.estimation_method && (
                  <div className="mt-4 pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-400 flex items-center gap-1">
                      <Info size={12} />
                      {analysis.cost_estimation.estimation_method}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ============================================================ */}
          {/* PREMIUM: Signaux de complexité */}
          {/* ============================================================ */}
          {analysis.cost_estimation?.complexity_signals?.length > 0 && (
            <Card className="border-orange-100">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <AlertTriangle size={18} className="text-orange-500" />
                  Signaux de complexité détectés
                  <span className="ml-auto text-sm font-normal text-slate-500 bg-orange-50 px-2 py-0.5 rounded">
                    {analysis.cost_estimation.complexity_signals.length} signal{analysis.cost_estimation.complexity_signals.length > 1 ? 'x' : ''}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analysis.cost_estimation.complexity_signals.map((signal, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-3 bg-orange-50/50 rounded-lg border border-orange-100"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                        <div>
                          <p className="text-sm font-medium text-slate-800 capitalize">
                            {signal.id?.replace(/_/g, ' ')}
                          </p>
                          {signal.keyword_matched && (
                            <p className="text-xs text-slate-500">
                              Détecté : "{signal.keyword_matched}"
                            </p>
                          )}
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-1 rounded">
                        +{signal.points} pts
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  Ces signaux influencent le coefficient de complexité appliqué à l'estimation.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Cost Estimation */}
          {analysis.cost_estimation && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Euro size={20} />
                  Estimation des coûts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-sm text-slate-500">Estimation basse</p>
                    <p className="text-xl font-bold text-slate-900">{analysis.cost_estimation.total_estimate?.low?.toLocaleString()} €</p>
                    <p className="text-xs text-slate-400">{analysis.cost_estimation.price_per_m2?.low} €/m²</p>
                  </div>
                  <div className="p-4 bg-primary/10 rounded-lg text-center border-2 border-primary">
                    <p className="text-sm text-primary font-medium">Estimation probable</p>
                    <p className="text-2xl font-bold text-primary">{analysis.cost_estimation.total_estimate?.mid?.toLocaleString()} €</p>
                    <p className="text-xs text-slate-500">{analysis.cost_estimation.price_per_m2?.mid} €/m²</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-sm text-slate-500">Estimation haute</p>
                    <p className="text-xl font-bold text-slate-900">{analysis.cost_estimation.total_estimate?.high?.toLocaleString()} €</p>
                    <p className="text-xs text-slate-400">{analysis.cost_estimation.price_per_m2?.high} €/m²</p>
                  </div>
                </div>

                {/* Cost breakdown */}
                {analysis.cost_estimation.cost_breakdown && (
                  <div className="p-4 bg-slate-50 rounded-lg mb-4">
                    <h4 className="font-medium text-slate-900 mb-3">Décomposition du prix</h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Déboursés secs</p>
                        <p className="font-medium">{analysis.cost_estimation.cost_breakdown.debourses_secs?.toLocaleString()} €</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Frais de chantier</p>
                        <p className="font-medium">{analysis.cost_estimation.cost_breakdown.frais_chantier?.toLocaleString()} €</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Frais généraux</p>
                        <p className="font-medium">{analysis.cost_estimation.cost_breakdown.frais_generaux?.toLocaleString()} €</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Marge</p>
                        <p className="font-medium">{analysis.cost_estimation.cost_breakdown.marge?.toLocaleString()} €</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Provision aléas</p>
                        <p className="font-medium">{analysis.cost_estimation.cost_breakdown.provision_aleas?.toLocaleString()} €</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Labor - Version enrichie avec détail low/mid/high */}
                {analysis.cost_estimation.labor && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Users size={18} className="text-blue-600" />
                        <span className="font-medium text-slate-900">Main d'œuvre estimée</span>
                      </div>
                      <span className="text-lg font-bold text-blue-700">
                        {analysis.cost_estimation.labor.total_hours?.toLocaleString()} heures
                      </span>
                    </div>
                    
                    {/* Détail low/mid/high si disponible */}
                    {analysis.cost_estimation.labor_hours_detail && (
                      <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-blue-100">
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Fourchette basse</p>
                          <p className="text-sm font-medium text-slate-700">
                            {analysis.cost_estimation.labor_hours_detail.low?.toLocaleString()} h
                          </p>
                        </div>
                        <div className="text-center bg-blue-100 rounded py-1">
                          <p className="text-xs text-blue-600">Estimation</p>
                          <p className="text-sm font-bold text-blue-700">
                            {analysis.cost_estimation.labor_hours_detail.mid?.toLocaleString()} h
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Fourchette haute</p>
                          <p className="text-sm font-medium text-slate-700">
                            {analysis.cost_estimation.labor_hours_detail.high?.toLocaleString()} h
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <p className="text-xs text-slate-400 mt-4 text-center">
                  {analysis.cost_estimation.disclaimer}
                </p>
              </CardContent>
            </Card>
          )}

          {/* ============================================================ */}
          {/* PREMIUM: Ventilation par corps d'état */}
          {/* ============================================================ */}
          {analysis.cost_estimation?.trade_breakdown?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Layers size={18} className="text-slate-500" />
                  Ventilation par corps d'état
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analysis.cost_estimation.trade_breakdown.map((trade, idx) => (
                    <div key={idx} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Hammer size={14} className="text-slate-400" />
                          <span className="text-sm font-medium text-slate-700">{trade.label}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-slate-900">
                            {trade.amount?.toLocaleString()} €
                          </span>
                          <span className="text-xs text-slate-400 ml-2">
                            ({trade.percentage}%)
                          </span>
                        </div>
                      </div>
                      {/* Barre de progression */}
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-primary/70 to-primary rounded-full transition-all duration-300"
                          style={{ width: `${Math.min(trade.percentage * 3, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Total */}
                <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-600">Total estimé (mid)</span>
                  <span className="text-lg font-bold text-primary">
                    {analysis.cost_estimation.total_estimate?.mid?.toLocaleString()} €
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Effort Estimation */}
          {analysis.effort_estimation && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <TrendingUp size={20} />
                  Effort de réponse estimé
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-3xl font-bold text-slate-900">{analysis.effort_estimation.total_hours}h</p>
                    <p className="text-sm text-slate-500">Effort total estimé</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    analysis.effort_estimation.effort_level === 'Faible' ? 'bg-green-100 text-green-700' :
                    analysis.effort_estimation.effort_level === 'Moyen' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {analysis.effort_estimation.effort_level}
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                  {analysis.effort_estimation.breakdown && Object.entries(analysis.effort_estimation.breakdown).map(([key, value]) => (
                    <div key={key} className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="font-medium text-slate-900">{value}h</p>
                    </div>
                  ))}
                </div>

                <p className="text-sm text-slate-600">{analysis.effort_estimation.recommendation}</p>
              </CardContent>
            </Card>
          )}

          {/* Required Documents */}
          {analysis.required_documents?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">Documents requis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-3">
                  {analysis.required_documents.map((doc, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${doc.status === 'OBLIGATOIRE' ? 'text-red-500' : 'text-slate-400'}`} />
                      <div>
                        <p className="font-medium text-slate-900">{doc.name}</p>
                        <p className="text-xs text-slate-500">{doc.source}</p>
                        {doc.note && <p className="text-xs text-primary mt-1">{doc.note}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reliability */}
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield size={18} className="text-slate-400" />
                <span className="text-sm text-slate-600">Fiabilité de l'analyse</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${analysis.reliability_score || 70}%` }} />
                </div>
                <span className="font-medium text-slate-900">{analysis.reliability_score || 70}%</span>
              </div>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <p className="text-xs text-slate-400 text-center">
            DCE Analyzer fournit une aide à la décision. Les recommandations doivent être validées par vos équipes.
          </p>
        </div>
      )}

      {/* Pending State */}
      {project.status === 'pending' && (
        <Card className="border-dashed border-2">
          <CardContent className="py-12 text-center">
            <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-900 mb-2">En attente d'analyse</h3>
            <p className="text-slate-500">L'analyse démarrera bientôt.</p>
          </CardContent>
        </Card>
      )}
    </Layout>
  );
}

// Helper component for traced info
function TracedInfoCard({ icon, title, data, getConfidenceBadge }) {
  if (!data) return null;
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {icon}
            {title}
          </CardTitle>
          {data.source && getConfidenceBadge(data.source.confidence)}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-xl font-bold text-slate-900">{data.value || 'Non détecté'}</p>
        {data.source && data.source.confidence !== 'NOT_FOUND' && (
          <div className="mt-2 p-3 bg-slate-50 rounded-lg text-sm">
            <p className="text-slate-500">Source: {data.source.document} – Page {data.source.page}</p>
            {data.source.excerpt && (
              <p className="text-slate-600 italic mt-1">"{data.source.excerpt}"</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
